// Audible alarm built from a Web Audio oscillator (no audio file).
// Browsers keep audio suspended until the page gets a user gesture, so we
// unlock on the first pointer/key press and report "blocked" until then.
// The same context also plays the interface sounds (boot chirp, module tick),
// which respect the Settings mute; the panic alarm never does.

import { soundMuted } from "../hud/sound";

type Listener = () => void;

const HIGH_HZ = 960;
const LOW_HZ = 720;
const STEP_S = 0.35;
const VOLUME = 0.16;
const MAX_MS = 60_000;

class Alarm {
  private ctx: AudioContext | null = null;
  private nodes: { osc: OscillatorNode; gain: GainNode } | null = null;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<Listener>();
  private wantPlaying = false;

  constructor() {
    if (typeof window === "undefined") return;
    const unlock = () => {
      void this.unlock();
    };
    window.addEventListener("pointerdown", unlock, { capture: true });
    window.addEventListener("keydown", unlock, { capture: true });
  }

  private context(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    this.ctx = new Ctor();
    this.ctx.addEventListener("statechange", () => this.emit());
    return this.ctx;
  }

  async unlock(): Promise<void> {
    const ctx = this.context();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {
        // Still locked; the next gesture will try again.
      }
    }
    // An alarm that arrived while locked starts as soon as we are allowed.
    if (this.wantPlaying && !this.nodes && ctx.state === "running") this.startNodes();
    this.emit();
  }

  get blocked(): boolean {
    const ctx = this.ctx;
    return this.wantPlaying && (!ctx || ctx.state !== "running");
  }

  get playing(): boolean {
    return this.wantPlaying;
  }

  start(): void {
    this.wantPlaying = true;
    if (this.stopTimer) clearTimeout(this.stopTimer);
    this.stopTimer = setTimeout(() => this.stop(), MAX_MS);
    const ctx = this.context();
    if (ctx && ctx.state !== "running") void ctx.resume().catch(() => undefined);
    if (ctx && ctx.state === "running" && !this.nodes) this.startNodes();
    if ("vibrate" in navigator) {
      try {
        navigator.vibrate([400, 200, 400, 200, 400]);
      } catch {
        // Not allowed without a gesture on some browsers.
      }
    }
    this.emit();
  }

  stop(): void {
    this.wantPlaying = false;
    if (this.stopTimer) clearTimeout(this.stopTimer);
    this.stopTimer = null;
    if (this.nodes && this.ctx) {
      const { osc, gain } = this.nodes;
      const t = this.ctx.currentTime;
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(gain.gain.value, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.05);
      osc.stop(t + 0.06);
    }
    this.nodes = null;
    this.emit();
  }

  /** Short two-note chirp used for "alert sent" and test feedback. */
  chirp(): void {
    const ctx = this.context();
    if (!ctx || ctx.state !== "running") return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(LOW_HZ, t);
    osc.frequency.setValueAtTime(HIGH_HZ, t + 0.09);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(VOLUME * 0.6, t + 0.01);
    gain.gain.setValueAtTime(VOLUME * 0.6, t + 0.17);
    gain.gain.linearRampToValueAtTime(0, t + 0.2);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.21);
  }

  /** Rising three-step chirp when the boot sequence finishes (interface sound). */
  bootChirp(): void {
    if (soundMuted()) return;
    const ctx = this.context();
    if (!ctx || ctx.state !== "running") return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(520, t);
    osc.frequency.exponentialRampToValueAtTime(1560, t + 0.22);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.07, t + 0.02);
    gain.gain.setValueAtTime(0.07, t + 0.16);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.36);
  }

  /** Soft mechanical tick on module switch (interface sound). */
  tick(): void {
    if (soundMuted()) return;
    const ctx = this.context();
    if (!ctx || ctx.state !== "running") return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(2200, t);
    osc.frequency.exponentialRampToValueAtTime(900, t + 0.03);
    gain.gain.setValueAtTime(0.035, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.045);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.05);
  }

  private startNodes(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    osc.type = "sawtooth";
    filter.type = "lowpass";
    filter.frequency.value = 2400;
    const steps = Math.ceil(MAX_MS / 1000 / STEP_S);
    for (let i = 0; i < steps; i++) {
      osc.frequency.setValueAtTime(i % 2 === 0 ? HIGH_HZ : LOW_HZ, t0 + i * STEP_S);
    }
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(VOLUME, t0 + 0.03);
    osc.connect(filter).connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + MAX_MS / 1000);
    osc.addEventListener("ended", () => {
      if (this.nodes?.osc === osc) {
        this.nodes = null;
        this.wantPlaying = false;
        this.emit();
      }
    });
    this.nodes = { osc, gain };
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    this.listeners.forEach((l) => l());
  }
}

export const alarm = new Alarm();
